import { useEffect, useRef, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import { route } from 'preact-router';
import i18next from 'i18next';
import { api } from '../api/client';
import { categories, categoriesLoaded } from '../stores/category.store';
import { DashboardConfigModal, loadSections, type DashSection } from '../components/DashboardConfig';
import { SkeletonBar, Spinner, Button, Badge } from '../components/ui';

import { T } from '../styles/tokens';
const POLL_INTERVAL = 30_000;

interface PopularAction { id: string; name: string; slug: string; icon: string; description: string; execution_mode: string; agent_name: string; category_id: string; run_count: number; }
interface RecentFile { file_path: string; size_bytes?: number; modified_at?: string; }
interface Deploy { id: number; name: string; repo: string; repo_full: string; status: string; branch: string; commit_sha: string; commit_message: string; actor: string; created_at: string; updated_at: string; duration_seconds: number | null; url: string; }
interface RunnerSummary { total: number; online: number; }

const popularActions = signal<PopularAction[]>([]);
const loadingActions = signal(true);
const recentFiles = signal<RecentFile[]>([]);
const loadingFiles = signal(true);
const deploys = signal<Deploy[]>([]);
const loadingDeploys = signal(true);
const deploysEnabled = signal(false);
const lastUpdated = signal<string>('');
const isRefreshing = signal(false);
const runnerStats = signal<RunnerSummary>({ total: 0, online: 0 });

const FILE_ICONS: Record<string, string> = {
 ts: '', tsx: '', js: '', json: '', md: '', html: '',
 css: '', sql: '', sh: '', yml: '', yaml: '', env: '',
 txt: '', py: '', gitignore: '',
};
function getFileIcon(name: string): string { const ext = name.split('.').pop()?.toLowerCase() || ''; return FILE_ICONS[ext] || ''; }
function formatTimeAgo(date?: string): string { if (!date) return ''; const diff = Date.now() - new Date(date).getTime(); const mins = Math.floor(diff / 60000); if (mins < 1) return 'agora'; if (mins < 60) return `${mins}min`; const hrs = Math.floor(mins / 60); if (hrs < 24) return `${hrs}h atrás`; return `${Math.floor(hrs / 24)}d atrás`; }
function formatSize(bytes?: number): string { if (!bytes) return ''; if (bytes < 1024) return `${bytes} B`; if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`; return `${(bytes / (1024 * 1024)).toFixed(1)} MB`; }

interface FileCat { name: string; icon: string; color: string; matcher: (p: string) => boolean }
const FILE_CATS: FileCat[] = [
 { name: i18next.t('agents:hub_brain'), icon: '', color: T.accent, matcher: (p) => { const n = p.split('/').pop()?.toLowerCase()||''; return ['agents.md','soul.md','user.md','identity.md','memory.md','tools.md','heartbeat.md','bootstrap.md'].includes(n) || n.startsWith('.env'); } },
 { name: i18next.t('agents:hub_memories'), icon: '', color: T.success, matcher: (p) => p.startsWith('memory/') || p.includes('/memory/') },
 { name: i18next.t('agents:hub_reports'), icon: '', color: T.warning, matcher: (p) => { const n=p.split('/').pop()?.toLowerCase()||''; return p.startsWith('relatorios/') || n.startsWith('changelog') || n.startsWith('relatorio') || (p.startsWith('docs/') && (n.endsWith('.md')||n.endsWith('.html'))); } },
 { name: i18next.t('agents:hub_logs'), icon: '', color: T.warning, matcher: (p) => { const n=p.split('/').pop()?.toLowerCase()||''; return p.includes('/logs/') || n.endsWith('.log') || n.includes('debug'); } },
 { name: i18next.t('agents:hub_scripts'), icon: '', color: T.accent, matcher: (p) => p.startsWith('scripts/') || p.includes('/scripts/') },
 { name: i18next.t('agents:hub_backups'), icon: '', color: T.accent, matcher: (p) => p.startsWith('backups/') || p.includes('backup') },
 { name: i18next.t('agents:hub_config'), icon: '', color: T.textMuted, matcher: (p) => { const n=p.split('/').pop()?.toLowerCase()||''; return ['package.json','tsconfig.json','.gitignore','.nvmrc','wrangler.toml'].includes(n); } },
 { name: i18next.t('agents:hub_code'), icon: '', color: T.primary, matcher: (p) => { const e=p.split('.').pop()?.toLowerCase()||''; return ['ts','tsx','js','jsx','py','sql','css'].includes(e); } },
 { name: i18next.t('agents:hub_docs'), icon: '', color: T.primary, matcher: (p) => { const e=p.split('.').pop()?.toLowerCase()||''; return ['html','pdf','doc'].includes(e); } },
];
function getFileCat(path: string): FileCat { return FILE_CATS.find(c => c.matcher(path)) || { name: i18next.t('agents:hub_others'), icon: '', color: T.textMuted, matcher: () => true }; }

async function fetchActions() { try { const d = await api.get<{ actions: PopularAction[] }>('/actions/popular'); popularActions.value = d.actions ?? []; } catch {} finally { loadingActions.value = false; } }
async function fetchDeploys() { try { const d = await api.get<{ deploys: Deploy[] }>('/integrations/github/deploys?limit=5'); if (d.deploys?.length) { deploys.value = d.deploys; deploysEnabled.value = true; } } catch {} finally { loadingDeploys.value = false; } }
async function fetchRunners() { try { const res = await api.get('/runner-v3/list') as any; const runners = res?.data?.runners || []; const online = runners.filter((r: any) => r.status === 'online' && r.last_seen_at && Date.now() - new Date(r.last_seen_at).getTime() < 120_000).length; runnerStats.value = { total: runners.length, online }; } catch {} }
async function fetchFiles() {
  try {
    let runners: any[]; try { runners = (await api.get<{ runners: any[] }>('/workspace/runners')).runners || []; } catch { runners = (await api.get<{ runners: any[] }>('/runners')).runners || []; }
    const online = runners.filter((r: any) => r.status === 'online');
    if (online.length > 0) { const data = await api.get<{ files: RecentFile[] }>(`/workspace/runners/${online[0].id}/files`); recentFiles.value = (data.files || []).filter((f: RecentFile) => f.modified_at).sort((a: RecentFile, b: RecentFile) => new Date(b.modified_at!).getTime() - new Date(a.modified_at!).getTime()).slice(0, 10); }
  } catch {} finally { loadingFiles.value = false; }
}
async function refreshAll(silent = false) { if (!silent) isRefreshing.value = true; await Promise.all([fetchActions(), fetchDeploys(), fetchFiles(), fetchRunners()]); lastUpdated.value = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); isRefreshing.value = false; }

export function HubPage() {
  const intervalRef = useRef<any>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [sections, setSections] = useState<DashSection[]>(() => loadSections());
  const isVisible = (id: string) => sections.find(s => s.id === id)?.enabled !== false;

  useEffect(() => {
    refreshAll();
    intervalRef.current = setInterval(() => refreshAll(true), POLL_INTERVAL);
    const onVis = () => { if (document.visibilityState === 'visible') refreshAll(true); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(intervalRef.current); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  if (!categoriesLoaded.value) return (<div style={{ display: 'grid', gap: 12 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
              <SkeletonBar width="60%" height={16} />
              <div style={{ height: 8 }} />
              <SkeletonBar width="80%" height={12} />
              <div style={{ height: 6 }} />
              <SkeletonBar width="40%" height={12} />
            </div>
          ))}
        </div>);

  return (
    <div style={{ position: 'relative' }}>
      {/* Aurora glow */}
      <div style={{ position: 'absolute', top: -200, left: '50%', transform: 'translateX(-50%)', width: 600, height: 400, background: `radial-gradient(ellipse, rgba(98,208,255,0.05) 0%, ${T.accentBgSubtle} 40%, transparent 70%)`, pointerEvents: 'none', zIndex: 0 }} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, position: 'relative', zIndex: 1 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: T.text, letterSpacing: '-0.5px' }}>Dashboard</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: T.textMuted }}>Visão geral do seu workspace</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {lastUpdated.value && <span style={{ fontSize: 11, color: T.textMuted, opacity: 0.7 }}>Atualizado {lastUpdated.value}</span>}
          <Button variant="ghost" size="xs" onClick={() => setConfigOpen(true)} title="Configurar" style={{ fontSize: 16, border: `1px solid ${T.primary}` }}>⋯</Button>
          <Button variant="ghost" size="xs" onClick={() => refreshAll()} disabled={isRefreshing.value} title="Atualizar" style={{ border: `1px solid ${T.primary}` }}>
 {isRefreshing.value ? <Spinner size="sm" inline /> : '🔄'}
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20, background: T.successBg, border: `1px solid ${T.successBg}` }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.success, boxShadow: '0 0 6px var(--success)', animation: 'pulse 2s infinite' }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: T.success }}>System Healthy</span>
          </div>
        </div>
      </div>

      <DashboardConfigModal open={configOpen} onClose={() => setConfigOpen(false)} onSave={(s) => setSections(s)} />

      {/* Stat Cards */}
      <div class="stat-cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 28, position: 'relative', zIndex: 1 }}>
        <StatCard label="Máquinas Ativas" value={`${runnerStats.value.online}`} sub={`de ${runnerStats.value.total} total`} accent={T.primary} onClick={() => route('/agents')} />
        <StatCard label="Ações Disponíveis" value={`${popularActions.value.length}`} sub="ações configuradas" accent={T.textSecondary} onClick={() => {}} />
        <StatCard label="Categorias" value={`${categories.value.length}`} sub="organizadas" accent={T.accent} onClick={() => {}} />
        {deploysEnabled.value && (() => {
          const s = deploys.value[0]?.status;
 const v = s === 'success' ? 'OK' : s === 'in_progress' ? i18next.t('agents:hub_uploading') : s === 'queued' ? i18next.t('agents:hub_queued') : s === 'failure' ? i18next.t('agents:hub_failure') : s || '—';
          const c = s === 'success' ? T.success : s === 'in_progress' || s === 'queued' ? T.warning : T.danger;
          return <StatCard label="Último Deploy" value={v} sub={formatTimeAgo(deploys.value[0]?.created_at)} accent={c} onClick={() => {}} />;
        })()}
      </div>

      {/* Sections */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {sections.map(sec => {
          if (!sec.enabled) return null;
          if (sec.id === 'actions' && !loadingActions.value && popularActions.value.length > 0) return <ActionsSection key="actions" />;
          if (sec.id === 'categories' && categories.value.length > 0) return <CategoriesSection key="categories" />;
          if (sec.id === 'deploys' && deploysEnabled.value && !loadingDeploys.value && deploys.value.length > 0) return <DeploysSection key="deploys" />;
          if (sec.id === 'files' && !loadingFiles.value && recentFiles.value.length > 0) return <FilesSection key="files" />;
          return null;
        })}
      </div>

      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}

/* ═══ Stat Card ═══ */
function StatCard({ label, value, sub, accent, onClick }: { label: string; value: string; sub: string; accent: string; onClick: () => void }) {
  return (
    <div onClick={onClick} class="stat-card" style={{
      padding: '20px', borderRadius: 14, cursor: 'pointer',
      background: T.bgCard, border: `1px solid ${T.primaryBg}`,
      transition: 'all 0.2s',
    }}
      onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.boxShadow = `0 0 20px ${T.primary}`; }}
      onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 800, color: accent, letterSpacing: '-1px', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4, lineHeight: 1.3 }}>{sub}</div>
    </div>
  );
}

/* ═══ Quick Actions ═══ */
function ActionsSection() {
  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14, color: T.text }}>Ações Rápidas</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
        {popularActions.value.map((action) => (
          <div key={action.id} onClick={() => route(`/actions/${action.id}`)} style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px',
            borderRadius: 14, cursor: 'pointer',
            background: `linear-gradient(135deg, ${T.primaryBg}, ${T.accentBgSubtle})`,
            border: `1px solid ${T.primary}`,
            transition: 'all 0.2s',
          }}
            onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.transform = 'none'; }}
          >
            <div style={{
              width: 42, height: 42, borderRadius: 12,
              background: T.primaryBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, flexShrink: 0,
 }}>{action.icon || ''}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{action.name}</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>{action.agent_name}</div>
            </div>
            {action.run_count > 0 && (
              <Badge variant="info">{action.run_count}×</Badge>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══ Categories ═══ */
function CategoriesSection() {
  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14, color: T.text }}>Categorias</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
        {categories.value.map((cat) => (
          <div key={cat.slug} onClick={() => route(`/categories/${cat.slug}`)} style={{
            padding: 20, borderRadius: 14, cursor: 'pointer',
            background: T.bgCard, border: `1px solid ${T.primaryBg}`,
            transition: 'all 0.2s',
          }}
            onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; }}
            onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.primary; }}
          >
            <div style={{ fontSize: 28, marginBottom: 10 }}>{cat.icon}</div>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, margin: '0 0 4px' }}>{cat.name}</h3>
            <p style={{ fontSize: 12, color: T.textMuted, margin: '0 0 12px', lineHeight: 1.4 }}>{cat.description}</p>
            <Badge variant="info">{cat.agent_count} ação(ões)</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══ Deploys ═══ */
function DeploysSection() {
  const statusInfo = (s: string) => {
    switch (s) {
      case 'success': return { color: T.success, bg: T.success, label: i18next.t('agents:hub_success'), dot: T.success };
 case 'failure': return { color: T.danger, bg: T.danger, label: i18next.t('agents:hub_failure'), dot: T.danger };
      case 'in_progress': return { color: T.warning, bg: T.warningBg, label: '⟳ Subindo...', dot: T.warning };
      case 'queued': return { color: T.purple, bg: T.purple, label: '◷ Na fila', dot: T.purple };
      case 'cancelled': return { color: T.textMuted, bg: T.bgActive, label: '— Cancelado', dot: T.textMuted };
      default: return { color: T.textSecondary, bg: T.bgActive, label: s || '?', dot: T.textSecondary };
    }
  };
  const fmtTime = (ts: string) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };
  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14, color: T.text }}>Últimos Deploys</h2>
      <div style={{ borderRadius: 14, border: `1px solid ${T.primary}`, overflow: 'hidden' }}>
        {deploys.value.map((d, i) => {
          const st = statusInfo(d.status);
          const dur = d.duration_seconds != null ? d.duration_seconds >= 60 ? `${Math.floor(d.duration_seconds / 60)}m ${d.duration_seconds % 60}s` : `${d.duration_seconds}s` : '';
          const isActive = d.status === 'in_progress' || d.status === 'queued';
          return (
            <a key={d.id} href={d.url} target="_blank" rel="noopener noreferrer" style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
              cursor: 'pointer', textDecoration: 'none', color: 'inherit',
              borderBottom: i < deploys.value.length - 1 ? `1px solid ${T.primary}` : 'none',
              transition: 'background 0.15s',
              background: isActive ? T.warningBg : 'transparent',
            }}
              onMouseEnter={(e: any) => { e.currentTarget.style.background = isActive ? T.warningBg : T.primaryBg; }}
              onMouseLeave={(e: any) => { e.currentTarget.style.background = isActive ? T.warningBg : 'transparent'; }}
            >
              {/* Status dot (animated for active) */}
              <span style={{
                width: 10, height: 10, borderRadius: '50%', background: st.dot, flexShrink: 0,
                ...(isActive ? { animation: 'pulse 1.5s ease-in-out infinite' } : {}),
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {d.commit_message || d.name}
                </div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {d.commit_sha && (
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: T.primary, background: T.primaryBg, padding: '1px 6px', borderRadius: 4 }}>
                      {d.commit_sha}
                    </span>
                  )}
                  <span style={{ color: T.accent, fontWeight: 600 }}>{d.branch}</span>
                  <span style={{ opacity: 0.5 }}>·</span>
                  <span>{d.repo}</span>
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: st.color, background: st.bg, padding: '2px 8px', borderRadius: 6, display: 'inline-block', marginBottom: 3 }}>
                  {st.label}
                </span>
                <div style={{ fontSize: 11, color: T.textMuted }}>{fmtTime(d.created_at)}</div>
 {dur && d.status === 'success' && <div style={{ fontSize: 10, color: T.textMuted, opacity: 0.5 }}> {dur}</div>}
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}

/* ═══ Recent Files ═══ */
function FilesSection() {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: T.text }}>Arquivos Recentes</h2>
        <span style={{ fontSize: 12, color: T.primary, cursor: 'pointer', fontWeight: 600 }} onClick={() => route('/files')}>Ver todos →</span>
      </div>
      <div style={{ borderRadius: 14, border: `1px solid ${T.primary}`, overflow: 'hidden' }}>
        {recentFiles.value.map((file, i) => {
          const name = file.file_path.split('/').pop() || file.file_path;
          const dir = file.file_path.split('/').slice(0, -1).join('/');
          const cat = getFileCat(file.file_path);
          return (
            <div key={file.file_path} onClick={() => route('/files')} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', cursor: 'pointer',
              borderBottom: i < recentFiles.value.length - 1 ? `1px solid ${T.primary}` : 'none',
              transition: 'background 0.15s',
            }}
              onMouseEnter={(e: any) => { e.currentTarget.style.background = T.primaryBg; }}
              onMouseLeave={(e: any) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: 16, flexShrink: 0 }}>{getFileIcon(name)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: cat.color, background: `${cat.color}12`, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', flexShrink: 0 }}>{cat.icon} {cat.name}</span>
                </div>
                {dir && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>{dir}</div>}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 11, color: T.textMuted }}>{formatTimeAgo(file.modified_at)}</div>
                <div style={{ fontSize: 10, color: T.textMuted, opacity: 0.5 }}>{formatSize(file.size_bytes)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
